/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Icon } from '@components/atoms'
import { DrawerSubMenuList } from './DrawerSubMenuList'
import { ListItemButton } from '@mui/material'

import { useAppDispatch, useAppSelector, mainActions, MenuType } from '@redux/index'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDrawerOpen } from '@components/systems/backoffice/BoShellContext'

interface PropsType {
	menu: MenuType
	isFirstDepth: boolean
}

/** 1·2깊이 항목. 링크(to)면 이동, 아니면 하위 펼침 */
export const DrawerMainMenuItem = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const location = useLocation()
	const navigate = useNavigate()

	// redux state
	const openedMenuIdArr: string[] = useAppSelector(state => state.main.openedMenuIdArr)
	const isOpenDrawer: boolean = useDrawerOpen()

	// variables
	const iconName: string =
		props.menu.icon !== undefined ? props.menu.icon : 'circle-medium'
	const samePathWithSubmenu: MenuType[] = React.useMemo(() => {
		if (props.menu.children !== undefined) {
			return props.menu.children.filter(
				aSubmenu => aSubmenu.to !== undefined && location.pathname === aSubmenu.to,
			)
		} else {
			return []
		}
	}, [props.menu.children, location.pathname])
	const isSubMenuSelected: boolean =
		props.menu.children !== undefined && samePathWithSubmenu.length > 0
	const isActive: boolean = location.pathname === props.menu.to
	const isOpened: boolean = openedMenuIdArr.includes(props.menu.id)

	// state
	const [isOpenSubmenu, setIsOpenSubmenu] = React.useState<boolean>(isOpened)
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

	React.useEffect(() => {
		if (!isOpenDrawer) {
			onCloseSubmenu()
			reduxDispatch(mainActions.OPENED_MENU_ID_ARR([]))
		}
	}, [isOpenDrawer])

	const onOpenSubmenuClick = React.useCallback(
		(event: any) => {
			const newOpenedMenuIdArr: string[] = isOpenDrawer
				? [...openedMenuIdArr]
				: [props.menu.id]
			setAnchorEl(event.currentTarget)

			if (isOpenDrawer) {
				if (openedMenuIdArr.includes(props.menu.id) && isOpenSubmenu) {
					// delete
					const idx = openedMenuIdArr.indexOf(props.menu.id)
					newOpenedMenuIdArr.splice(idx, 1)
				} else if (!openedMenuIdArr.includes(props.menu.id) && !isOpenSubmenu) {
					// add
					newOpenedMenuIdArr.push(props.menu.id)
				}
			}

			reduxDispatch(mainActions.OPENED_MENU_ID_ARR([...newOpenedMenuIdArr]))
			setIsOpenSubmenu(!isOpenSubmenu)
		},
		[isOpenDrawer, openedMenuIdArr, props.menu.id],
	)

	const onNavigateClick = React.useCallback((menu: MenuType) => {
		if (menu.to !== undefined) {
			navigate(menu.to)
		}
	}, [])

	const onCloseSubmenu = React.useCallback(() => {
		setAnchorEl(null)
		setIsOpenSubmenu(false)
	}, [])

	const hasChildren: boolean = props.menu.children !== undefined
	const showText: boolean = isOpenDrawer || !props.isFirstDepth
	const className: string = [
		'boMenuItem',
		props.isFirstDepth ? '' : 'depth2',
		isActive ? 'active' : '',
		isSubMenuSelected ? 'parentActive' : '',
		isOpenSubmenu && hasChildren ? 'expanded' : '',
		props.isFirstDepth && !isOpenDrawer ? 'centered' : '',
	].join(' ')

	return (
		<View css={style}>
			<ListItemButton
				id={`${props.menu.id}_button`}
				aria-controls={isOpenSubmenu ? `${props.menu.id}_menu` : undefined}
				aria-haspopup="true"
				aria-expanded={isOpenSubmenu ? 'true' : undefined}
				className={className}
				onClick={e =>
					props.menu.to !== undefined
						? onNavigateClick(props.menu)
						: onOpenSubmenuClick(e)
				}
			>
				{props.isFirstDepth || props.menu.icon !== undefined ? (
					<Icon name={iconName} className="boMenuIcon" />
				) : null}
				{showText ? (
					<Text className="boMenuText" as="span" i18n={props.menu.i18n}>
						{props.menu['name']}
					</Text>
				) : null}
				{showText && hasChildren ? (
					<Icon name="chevron-right" className="boMenuCaret" />
				) : null}
			</ListItemButton>

			<DrawerSubMenuList
				menu={props.menu}
				anchorEl={anchorEl}
				isOpenSubmenu={isOpenSubmenu}
				onCloseSubmenu={onCloseSubmenu}
				onNavigateClick={onNavigateClick}
			/>
		</View>
	)
}

const style = css({
	display: 'block',
})
